import api from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/constants';

export interface Stock {
  id: number;
  item_id: number;
  branch_id: number;
  qty: number;
  updated_at: string;
}

export interface StockWithDetails extends Stock {
  item: {
    id: number;
    sku: string;
    name: string;
    category: string;
    unit: string;
    cost_price: number;
    sell_price: number;
    min_stock: number;
  };
  branch: {
    id: number;
    code: string;
    name: string;
  };
}

export interface RestockRequest {
  item_id: number;
  branch_id: number;
  qty: number;
  notes?: string;
}

export interface BranchStockItem {
  item_id: number;
  sku: string;
  name: string;
  category: string;
  unit: string;
  sell_price: number;
  qty: number;
  min_stock: number;
  is_low_stock: boolean;
}

export interface LowStockItem {
  item_id: number;
  sku: string;
  name: string;
  branch_id: number;
  branch_code: string;
  current_qty: number;
  min_stock: number;
  shortage: number;
}

export interface StockLog {
  id: number;
  item_id: number;
  branch_id: number;
  qty_before: number;
  qty_change: number;
  qty_after: number;
  ref_type: string;
  ref_id?: number;
  user_id: number;
  notes?: string;
  created_at: string;
}

export interface StockForecast {
  item_id: number;
  item_name: string;
  sku: string;
  current_stock: number;
  min_stock: number;
  forecast_period_days: number;
  avg_daily_usage: number;
  adjusted_daily_usage: number;
  predicted_usage: number;
  predicted_stock_end: number;
  days_until_stockout: number | null;
  reorder_recommended: boolean;
  reorder_point: number;
  recommended_order_qty: number;
  confidence: 'low' | 'medium' | 'high';
  trend: 'increasing' | 'decreasing' | 'stable' | 'new' | 'insufficient_data';
  trend_percentage: number;
  days_of_data: number;
  message: string;
}

export interface BranchForecast {
  branch_id: number;
  forecast_period_days: number;
  total_items: number;
  items_needing_reorder: number;
  forecasts: StockForecast[];
}

const API_URL = `${API_BASE_URL}/stocks`;

// Get stock for a specific branch
export const getBranchStock = async (
  branchId: number,
  params?: {
    category?: string;
    low_stock_only?: boolean;
  }
): Promise<BranchStockItem[]> => {
  const response = await api.get<BranchStockItem[]>(
    `${API_URL}/branch/${branchId}`,
    { params }
  );
  return response.data;
};

// Get low stock items (all branches or specific branch)
export const getLowStockItems = async (
  branchId?: number
): Promise<LowStockItem[]> => {
  const response = await api.get<LowStockItem[]>(`${API_URL}/low-stock`, {
    params: branchId ? { branch_id: branchId } : undefined,
  });
  return response.data;
};

// Restock item (add stock)
export const restockItem = async (
  data: RestockRequest
): Promise<Stock> => {
  const response = await api.post<Stock>(`${API_URL}/restock`, data);
  return response.data;
};

// Adjust stock (stock opname)
export const adjustStock = async (
  itemId: number,
  branchId: number,
  newQty: number,
  notes: string
): Promise<Stock> => {
  const response = await api.post<Stock>(`${API_URL}/adjust`, null, {
    params: {
      item_id: itemId,
      branch_id: branchId,
      new_qty: newQty,
      notes,
    },
  });
  return response.data;
};

// Get stock history for an item at a branch
export const getStockHistory = async (
  itemId: number,
  branchId: number,
  limit: number = 50
): Promise<StockLog[]> => {
  const response = await api.get<StockLog[]>(
    `${API_URL}/history/${itemId}/${branchId}`,
    { params: { limit } }
  );
  return response.data;
};

// Legacy methods (kept for backward compatibility)
export const getStocks = async (params?: {
  branch_id?: number;
  item_id?: number;
  low_stock_only?: boolean;
}): Promise<StockWithDetails[]> => {
  const response = await api.get<StockWithDetails[]>(API_URL, { params });
  return response.data;
};

export const getStockByBranchAndItem = async (
  branchId: number,
  itemId: number
): Promise<Stock> => {
  const response = await api.get<Stock>(
    `${API_URL}/branch/${branchId}/item/${itemId}`
  );
  return response.data;
};

// Export stock report to Excel
export const exportStockExcel = async (
  branchId: number,
  category?: string
): Promise<Blob> => {
  const response = await api.get(`${API_URL}/export/excel/${branchId}`, {
    params: category ? { category } : undefined,
    responseType: 'blob',
  });
  return response.data;
};

// Export stock report to PDF
export const exportStockPDF = async (
  branchId: number,
  category?: string
): Promise<Blob> => {
  const response = await api.get(`${API_URL}/export/pdf/${branchId}`, {
    params: category ? { category } : undefined,
    responseType: 'blob',
  });
  return response.data;
};

// Get stock forecast for branch
export const getBranchForecast = async (
  branchId: number,
  daysAhead: number = 30,
  criticalOnly: boolean = false
): Promise<BranchForecast> => {
  const response = await api.get<BranchForecast>(
    `${API_URL}/forecast/${branchId}`,
    {
      params: {
        days_ahead: daysAhead,
        critical_only: criticalOnly,
      },
    }
  );
  return response.data;
};

// Get forecast for specific item
export const getItemForecast = async (
  branchId: number,
  itemId: number,
  daysAhead: number = 30
): Promise<StockForecast> => {
  const response = await api.get<StockForecast>(
    `${API_URL}/forecast/${branchId}/item/${itemId}`,
    {
      params: { days_ahead: daysAhead },
    }
  );
  return response.data;
};
